/**
 * Account service — centralises auth, account management, and signup API calls.
 *
 * Covers: login/logout, password reset, token operations, account CRUD,
 * signup flows, email/username uniqueness checks, and subscription info.
 */
import api from './api'

// ---------------------------------------------------------------------------
// Authentication
// ---------------------------------------------------------------------------

export function login(login, password) {
  return api.post('/auth/login', { login, password })
}

// Page-reload re-hydration (issue #901, PR-12). Returns the same payload
// shape as POST /auth/login: { token, account_id, capabilities, distributor }.
// Backend authenticates via the Authorization header (handled by axios
// interceptor); a 401 here means the stored token is invalid/expired.
export function authMe(config) {
  return config ? api.get('/auth/me', config) : api.get('/auth/me')
}

// Revokes the bearer token server-side (deletes the token row; for an
// anonymous storefront account also its resume codes). Must be sent BEFORE
// the local clear removes the token it authenticates with. The timeout keeps
// a hung request from holding up a logout that must happen regardless.
export function logout() {
  // The token may already be dead: a 401 here must not start auth recovery
  // or raise an error toast in the middle of a logout.
  return api.post('/auth/logout', {}, {
    timeout: 5000,
    skipAuthRecovery: true,
    suppressErrorToast: true,
  })
}

export function getToken(accountId) {
  return api.post(`/auth/getToken/${accountId}`)
}

// ---------------------------------------------------------------------------
// Password Reset
// ---------------------------------------------------------------------------

export function sendResetPasswordToken(email) {
  return api.post('/auth/sendResetPasswordToken', { email })
}

export function checkResetPasswordToken(token) {
  return api.get(`/auth/checkResetPasswordToken/${token}`)
}

export function resetPassword(data) {
  return api.post('/auth/resetPassword', data)
}

// ---------------------------------------------------------------------------
// Signup & Onboarding
// ---------------------------------------------------------------------------

export function signUpSalesRep(data) {
  return api.post('/auth/signUpSalesRep', data)
}

export function checkSignUpToken(token) {
  return api.get(`/auth/checkSignUpToken/${token}`)
}

export function publicAddAccount(data) {
  return api.post('/auth/publicAddAccount', data)
}

export function resendSignUpEmailToDM(data) {
  return api.post('/auth/resendSignUpEmailToDM', data)
}

export function createTeamTransaction(data) {
  return api.post('/auth/createTeamTransaction', data)
}

// ---------------------------------------------------------------------------
// Uniqueness Checks
// ---------------------------------------------------------------------------

export function getEmailUnique(email) {
  return api.post('/auth/getEmailUnique', { email })
}

export function getDistributorNameUnique(name) {
  return api.post('/auth/getDistributorNameUnique', { name })
}

export function getTeammatesEmailsUnique(teammates) {
  return api.post('/auth/getTeammatesEmailsUnique', { teammates })
}

// ---------------------------------------------------------------------------
// Subscription Info (via auth endpoints)
// ---------------------------------------------------------------------------

export function getSubscriptionInfo(id) {
  return api.get(`/auth/getSubscriptionInfo/${id}`)
}

export function getSubscriptionByName(name) {
  return api.get(`/auth/getSubscriptionByName/${name}`)
}

export function getActiveSubscriptions() {
  return api.get('/auth/getActiveSubscriptions')
}

export function getCountryCodes() {
  return api.get('/auth/getCountryCodes')
}

// ---------------------------------------------------------------------------
// Account CRUD
// ---------------------------------------------------------------------------

export function getAccountList(filterUsername, filterEmail, filterDistributor, filterCampaign, filterRole, filterActive, orderBy, limit) {
  return api.get(
    `/account/getAccountList/${filterUsername}/${filterEmail}/${filterDistributor}/${filterCampaign}/${filterRole}/${filterActive}/${orderBy}/${limit}`
  )
}

export function addAccount(account) {
  return api.post('/account/addAccount', { account })
}

export function activeAccount(accountId, active) {
  return api.get(`/account/activeAccount/${accountId}/${active}`)
}

export function deleteAccount(campaignId, accountId) {
  return api.get(`/account/deleteAccount/${campaignId}/${accountId}`)
}

export function getUserRoleIdById(accountId) {
  return api.get(`/account/getUserRoleIdById/${accountId}`)
}

export function getOrderCountByAccountId(accountId) {
  return api.get(`/account/getOrderCountByAccountId/${accountId}`)
}

// ---------------------------------------------------------------------------
// SAFRS / JSON:API model endpoints
// ---------------------------------------------------------------------------

export function setAccountCampaign(accountId, args) {
  return api.post(`/accounts/${accountId}/set_account_campaign`, {
    meta: { method: 'set_account_campaign', args },
  })
}
